'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Turnstile } from '@marsidev/react-turnstile';
import { SampleReportPreview } from '@/components/onboarding/SampleReportPreview';
import { createBrowserClient } from '@/lib/supabase-clients';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, ArrowRight, Check, Loader2, MessageSquare, Phone } from 'lucide-react';
import { ScheduleEditor } from '@/components/routines/ScheduleEditor';
import {
  InviteByEmailSection,
  buildInvitePayloadEntries,
  isInviteSuccessStatus,
  isBadPhoneReason,
  INVITE_BATCH_CAP,
  type InviteEntry,
  type InviteRowOutcome,
} from '@/components/routines/InviteByEmailSection';
import type { RoutineTemplate, RoutineQuestion } from '@/lib/services/routine-template-service';
import { wizardScheduleSeed, type RoutineDraftSpec } from '@/lib/onboarding/draft-spec';
import { sanitizeCampaign, sanitizeChannelIntent, channelsForIntent, type ChannelIntent } from '@/lib/campaign';

interface PersonaOption {
  key: string;
  label: string;
  description: string;
}

interface PublicRoutineWizardProps {
  template: RoutineTemplate;
  personaOptions: PersonaOption[];
  /** Server-resolved: a session exists AND the profile has a domain. */
  authed: boolean;
  /** Onboarding draft-spec (?draft=<token>), server-fetched and already
   * sanitized (see onboarding-draft-service trust contract). Seeds content
   * fields on first mount; the sessionStorage precedence rule below decides
   * whether it applies. */
  draftSpec?: RoutineDraftSpec;
  /** The claim token that produced draftSpec. Persisted into the
   * sessionStorage draft as a marker so a login round-trip can tell "my own
   * edited copy of this same server draft" from "a different/new draft". */
  draftToken?: string;
}

type Step = 'customize' | 'reach' | 'questions' | 'people' | 'schedule' | 'review';
const STEPS: Step[] = ['customize', 'reach', 'questions', 'people', 'schedule', 'review'];

// Everything the visitor typed, persisted to sessionStorage so the wizard
// survives the OTP/login round-trip of the taken-email path.
interface PublicRoutineDraft {
  step: Step;
  name: string;
  description: string;
  purpose: string;
  persona: string;
  durationMinutes: number;
  questions: RoutineQuestion[];
  inviteEntries: InviteEntry[];
  scheduleCron: string;
  scheduleNatural: string;
  timezone: string;
  email: string;
  /** Marketing attribution (e.g. 'member-touch') captured from ?campaign= on
   * arrival. Persisted with the draft so it survives the OTP/login round-trip,
   * then stamped into the anon signup metadata (auth.users.raw_user_meta_data)
   * alongside created_via — the K-factor funnel's v1 cohort marker. */
  campaign?: string;
  /** Reach intent from the 'reach' step. Persisted so the OTP/login
   * round-trip can't silently revert the routine to the template's channels. */
  channelIntent?: ChannelIntent;
  /** True once the user CLICKED a reach card. An explicit choice must beat a
   * ?channel= param on remount — the URL only preselects untouched wizards. */
  reachTouched?: boolean;
  /** Claim token of the onboarding draft-spec this draft was seeded from (if
   * any). The precedence marker: a sessionStorage draft carrying the SAME
   * token as the incoming ?draft= is the user's edited copy and wins; a
   * mismatch means a different server draft arrived and reseeds. */
  draftToken?: string;
}

type InviteSendResult = 'none' | 'sent' | 'blocked_unverified' | 'failed';

// ?campaign= flows verbatim into auth.users.raw_user_meta_data on the anon
// signup — validation lives in the shared lib/campaign.ts contract (see its
// cookie-bloat rationale; every producer/consumer imports the same check).

export function PublicRoutineWizard({
  template,
  personaOptions,
  authed,
  draftSpec,
  draftToken,
}: PublicRoutineWizardProps) {
  const router = useRouter();
  const draftKey = `public-routine-draft:${template.slug}`;

  // True when the wizard's content came from an onboarding draft-spec (either
  // seeded fresh this mount, or restored as the user's edited copy of the same
  // server draft). Drives the "drafted for you" banner.
  const [draftApplied, setDraftApplied] = useState(false);
  // The token persisted into the sessionStorage draft as the precedence
  // marker — see PublicRoutineDraft.draftToken.
  const [activeDraftToken, setActiveDraftToken] = useState<string | undefined>(undefined);

  const [step, setStep] = useState<Step>('customize');
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description || '');
  const [purpose, setPurpose] = useState('');
  const [persona, setPersona] = useState(template.agent_persona || 'mentor');
  const [durationMinutes, setDurationMinutes] = useState(template.default_duration_minutes);
  const [questions, setQuestions] = useState<RoutineQuestion[]>(template.default_questions || []);
  const [inviteEntries, setInviteEntries] = useState<InviteEntry[]>([]);
  const [scheduleCron, setScheduleCron] = useState(template.default_schedule_cron || '');
  const [scheduleNatural, setScheduleNatural] = useState('');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [campaign, setCampaign] = useState<string | null>(null);
  // Reach intent: which surface Tessa leads with. Defaults by the template's
  // own promise (phone template → calls); the mount effect below layers the
  // draft value and then ?channel= on top — but the URL only PRESELECTS: a
  // choice the user explicitly clicked (reachTouched) always beats it.
  const [channelIntent, setChannelIntent] = useState<ChannelIntent>(
    template.action_channels?.includes('phone') ? 'calls' : 'async',
  );
  const [reachTouched, setReachTouched] = useState(false);

  // Email gate (anon path) state. anonSessionReady / emailAttached checkpoint
  // the two auth steps so a retry after a downstream failure never re-runs
  // them: a second signInAnonymously would orphan a user, and a second
  // updateUser({email}) trips Supabase's 60s email rate limit.
  const [email, setEmail] = useState('');
  const [gateError, setGateError] = useState<string | null>(null);
  const [emailTaken, setEmailTaken] = useState(false);
  const [anonSessionReady, setAnonSessionReady] = useState(false);
  const [emailAttached, setEmailAttached] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  // Remount key for the Turnstile widget: a consumed token can't be reused, so
  // after a failed signInAnonymously the widget is remounted for a fresh solve
  // (otherwise the submit button stays disabled until the widget self-expires).
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const captchaSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const captchaRequired = !!captchaSiteKey;

  // Draft hydration/persistence. Hydrate once on mount (sessionStorage is
  // browser-only, so this cannot run in the useState initializers), THEN start
  // persisting — the `hydrated` flag keeps the very first render's template
  // defaults from clobbering a draft we are about to restore.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Set during hydration below; the ?channel= read further down consults it —
    // a reach card the user explicitly clicked must survive the round-trip
    // even when the return URL still carries the landing page's param.
    let draftReachExplicit = false;
    // Whether the sessionStorage draft was restored. When an onboarding
    // draft-spec (?draft=) is also present, the PRECEDENCE RULE is:
    //   storage token === incoming token → storage wins (it is the user's
    //     edited copy of this very server draft — the login round-trip case);
    //   mismatch/absent token on the storage draft while a spec is incoming →
    //     the spec wins (a different server draft arrived; stale local state
    //     must not shadow it) and the storage draft is discarded below.
    let restoredFromStorage = false;
    try {
      const raw = sessionStorage.getItem(draftKey);
      if (raw) {
        const draft = JSON.parse(raw) as Partial<PublicRoutineDraft>;
        const storageMatchesIncoming = !draftToken || draft?.draftToken === draftToken;
        if (draft && typeof draft === 'object' && storageMatchesIncoming) {
          if (typeof draft.name === 'string') setName(draft.name);
          if (typeof draft.description === 'string') setDescription(draft.description);
          if (typeof draft.purpose === 'string') setPurpose(draft.purpose);
          if (typeof draft.persona === 'string' && draft.persona) setPersona(draft.persona);
          if (typeof draft.durationMinutes === 'number' && Number.isFinite(draft.durationMinutes)) {
            setDurationMinutes(draft.durationMinutes);
          }
          if (Array.isArray(draft.questions)) {
            setQuestions(
              draft.questions.filter(
                (q): q is RoutineQuestion =>
                  !!q && typeof q.order === 'number' && typeof q.question === 'string',
              ),
            );
          }
          if (Array.isArray(draft.inviteEntries)) {
            setInviteEntries(
              draft.inviteEntries
                .filter(
                  (e): e is InviteEntry =>
                    !!e &&
                    typeof e.key === 'string' &&
                    typeof e.email === 'string' &&
                    typeof e.name === 'string' &&
                    typeof e.phone === 'string',
                )
                .slice(0, INVITE_BATCH_CAP),
            );
          }
          if (typeof draft.scheduleCron === 'string') setScheduleCron(draft.scheduleCron);
          if (typeof draft.scheduleNatural === 'string') setScheduleNatural(draft.scheduleNatural);
          if (typeof draft.timezone === 'string' && draft.timezone) setTimezone(draft.timezone);
          if (typeof draft.email === 'string') setEmail(draft.email);
          {
            // Re-sanitize on rehydration too — a draft persisted before this
            // guard existed (or hand-edited storage) must not smuggle an
            // unbounded value back in.
            const draftCampaign = sanitizeCampaign(draft.campaign);
            if (draftCampaign) setCampaign(draftCampaign);
          }
          {
            // Closed-enum re-check (hand-edited storage, pre-guard drafts).
            const draftIntent = sanitizeChannelIntent(draft.channelIntent);
            if (draftIntent) {
              setChannelIntent(draftIntent);
              if (draft.reachTouched === true) {
                setReachTouched(true);
                draftReachExplicit = true;
              }
            }
          }
          if (typeof draft.step === 'string' && (STEPS as string[]).includes(draft.step)) {
            setStep(draft.step as Step);
          }
          restoredFromStorage = true;
          if (draftToken && draft.draftToken === draftToken) {
            // The user's edited copy of the incoming server draft — keep the
            // banner up and keep persisting under the same marker.
            setDraftApplied(true);
            setActiveDraftToken(draftToken);
          } else if (typeof draft.draftToken === 'string') {
            setActiveDraftToken(draft.draftToken);
          }
        }
      }
    } catch (err) {
      console.error('[PublicRoutineWizard] Failed to hydrate draft:', err);
    }

    // Onboarding draft-spec seeding — only when storage didn't win. The spec
    // arrives pre-sanitized (server-side trust contract), so values are set
    // verbatim; fields the spec omits keep the template defaults.
    if (!restoredFromStorage && draftSpec) {
      if (draftSpec.name) setName(draftSpec.name);
      if (draftSpec.description) setDescription(draftSpec.description);
      if (draftSpec.purpose) setPurpose(draftSpec.purpose);
      if (draftSpec.questions && draftSpec.questions.length > 0) {
        setQuestions(draftSpec.questions);
      }
      // The rhythm the visitor was told was saved ("Saved: When: Mondays
      // 8:00 am, Vancouver time" in the thread, or what Tessa read back on
      // the call) must be the rhythm the wizard opens on — the template
      // default + the browser zone here would silently undo it. Fields the
      // spec omits keep the default (wizardScheduleSeed).
      {
        const rhythm = wizardScheduleSeed(draftSpec);
        if (rhythm.scheduleCron) setScheduleCron(rhythm.scheduleCron);
        if (rhythm.scheduleNatural) setScheduleNatural(rhythm.scheduleNatural);
        if (rhythm.timezone) setTimezone(rhythm.timezone);
      }
      if (draftSpec.channelIntent) {
        setChannelIntent(draftSpec.channelIntent);
        // An interview/inference-derived intent outranks a landing-page
        // ?channel= preselect (it came from the visitor's own words), so mark
        // it explicit for the URL read below.
        draftReachExplicit = true;
        setReachTouched(true);
      }
      if (draftSpec.ownerEmail) setEmail(draftSpec.ownerEmail);
      // Names the visitor mentioned to Tessa pre-seed the invite rows — name
      // only, contact fields empty (the interviewer never collects those).
      // The wizard feels like a continuation: "Maria — add her number". Rows
      // left without an email are dropped by the existing submit filter.
      if (draftSpec.inviteNames && draftSpec.inviteNames.length > 0) {
        setInviteEntries(
          draftSpec.inviteNames.slice(0, INVITE_BATCH_CAP).map((inviteName, i) => ({
            key: `draft-invite-${i}-${inviteName}`,
            email: '',
            name: inviteName,
            phone: '',
          })),
        );
      }
      setDraftApplied(true);
      setActiveDraftToken(draftToken);
    }
    // Campaign capture: a fresh ?campaign= arrival wins over any stale draft
    // value. Read from window.location (this effect is mount-only and
    // browser-only) so the param is caught before it can be lost to in-app
    // navigation; the debounced persist below folds it into the draft.
    // Sanitized — malformed/oversized values are dropped, not truncated.
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = sanitizeCampaign(params.get('campaign'));
      if (fromUrl) setCampaign(fromUrl);
      // ?channel= — the landing page's promise ("Tessa calls" vs async) —
      // PRESELECTS the reach card. Closed enum; junk is dropped so the
      // template default stands. A choice the user explicitly clicked
      // (draftReachExplicit) outranks it: preselection never reverts a human.
      const intentFromUrl = sanitizeChannelIntent(params.get('channel'));
      if (intentFromUrl && !draftReachExplicit) setChannelIntent(intentFromUrl);
    } catch {
      // Attribution is best-effort — never block the wizard.
    }
    setHydrated(true);
    // draftSpec/draftToken are server-render props, stable for the life of
    // this mount — hydration must run exactly once per template, so they are
    // deliberately not dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  const buildDraft = (): PublicRoutineDraft => ({
    step,
    name,
    description,
    purpose,
    persona,
    durationMinutes,
    questions,
    inviteEntries,
    scheduleCron,
    scheduleNatural,
    timezone,
    email,
    campaign: campaign ?? undefined,
    channelIntent,
    reachTouched,
    draftToken: activeDraftToken,
  });

  const persistDraftNow = () => {
    try {
      sessionStorage.setItem(draftKey, JSON.stringify(buildDraft()));
    } catch {
      // Storage unavailable (private mode quota, etc.) — the wizard still works,
      // it just won't survive a round-trip.
    }
  };

  // Debounced persist on every state change (no dep array on purpose — the
  // timeout resets each render and fires 300ms after the state settles).
  useEffect(() => {
    if (!hydrated) return;
    const handle = setTimeout(persistDraftNow, 300);
    return () => clearTimeout(handle);
  });

  const clearDraft = () => {
    try {
      sessionStorage.removeItem(draftKey);
    } catch {
      // Best effort.
    }
  };

  const currentStepIndex = STEPS.indexOf(step);

  const goNext = () => {
    if (currentStepIndex < STEPS.length - 1) setStep(STEPS[currentStepIndex + 1]);
  };
  const goBack = () => {
    if (currentStepIndex > 0) setStep(STEPS[currentStepIndex - 1]);
  };

  const selectedPersona = personaOptions.find((p) => p.key === persona);
  const inviteCount = buildInvitePayloadEntries(inviteEntries).length;

  // The invite endpoint rejects requests above INVITE_BATCH_CAP rows — block
  // adding past the cap here rather than failing the whole batch at create.
  const [inviteCapHit, setInviteCapHit] = useState(false);
  const handleInviteEntriesChange = (entries: InviteEntry[]) => {
    if (entries.length > INVITE_BATCH_CAP) {
      setInviteEntries(entries.slice(0, INVITE_BATCH_CAP));
      setInviteCapHit(true);
    } else {
      setInviteEntries(entries);
      if (entries.length < INVITE_BATCH_CAP) setInviteCapHit(false);
    }
  };

  const postRoutine = () =>
    fetch('/api/routines', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        templateSlug: template.slug,
        name,
        description,
        purpose,
        agentPersona: persona,
        durationMinutes,
        questions,
        scheduleCron,
        scheduleNatural,
        timezone,
        // Reach intent → action_channels, sent EXPLICITLY for both intents:
        // 'calls' promotes phone to the front, 'async' demotes phone/sms out
        // (channelsForIntent) — relying on the template default would make
        // async a no-op on phone-first templates.
        actionChannels: channelsForIntent(channelIntent, template.action_channels),
      }),
    });

  const sendInvites = async (routineId: string): Promise<InviteSendResult> => {
    const inviteRows = buildInvitePayloadEntries(inviteEntries);
    if (inviteRows.length === 0) return 'none';
    try {
      // No preferredChannels on purpose: the invite route inherits the
      // routine's own action_channels (members/invite/route.ts), which the
      // reach step just set — so member LADDERS (what dispatch actually
      // walks) match the intent card instead of the template's first channel.
      const inviteRes = await fetch(`/api/routines/${routineId}/members/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries: inviteRows }),
      });
      if (inviteRes.ok) {
        const data = await inviteRes.json();
        const outcomes: InviteRowOutcome[] = Array.isArray(data?.results) ? data.results : [];
        const attention = outcomes.filter((o) => !isInviteSuccessStatus(o.status));
        if (attention.length > 0) {
          const lines = attention.map((o) => {
            if (o.status === 'seat_exhausted_pending') return `${o.email}: invite link sent (no seats free)`;
            if (o.status === 'invalid') return `${o.email}: ${isBadPhoneReason(o.reason) ? 'invalid phone' : 'invalid email'}`;
            return `${o.email}: failed${o.reason ? ` (${o.reason})` : ''}`;
          });
          alert(`Routine created, but ${attention.length} email invite${attention.length === 1 ? ' needs' : 's need'} attention:\n\n${lines.join('\n')}`);
        }
        return 'sent';
      }
      // The invite endpoint is verified-email-gated (requireVerifiedEmail), so a
      // fresh Pattern-A creator lands here until they confirm their address.
      // The rows they typed must NOT be dropped: stash them under a
      // per-routine key; the routine detail page re-fires the exact same POST
      // automatically once the owner's email is confirmed.
      if (inviteRes.status === 403) {
        const data = await inviteRes.json().catch(() => null);
        if (data?.error === 'EMAIL_NOT_VERIFIED') {
          // localStorage, NOT sessionStorage: the confirm link typically opens
          // in a fresh tab, and the stash must survive that hop.
          try {
            // No preferredChannels here either — the detail page re-fires the
            // stash verbatim, and omission means the ladder inherits the
            // routine's reach-intent channels at send time.
            localStorage.setItem(
              `pending-routine-invites:${routineId}`,
              JSON.stringify({ entries: inviteRows }),
            );
          } catch {
            // storage unavailable — the alert below still tells them the manual path
          }
          return 'blocked_unverified';
        }
      }
      return 'failed';
    } catch (inviteErr) {
      console.error('[PublicRoutineWizard] Error sending email invites:', inviteErr);
      return 'failed';
    }
  };

  // Shared tail of both paths: create the routine, send invites, clear the
  // draft, land on the routine page. `anon` controls the auth-error retry and
  // the redirect flavor (hard navigation + verify note for a fresh account);
  // `viaGate` routes errors to the gate's inline message instead of alert()
  // (a signed-in user can reach the gate when the SSR authed check missed).
  const finishCreate = async ({ anon, viaGate }: { anon: boolean; viaGate: boolean }): Promise<void> => {
    let res = await postRoutine();

    // Provisioning read-lag guard: the DB trigger creates public.users + the
    // personal domain synchronously inside updateUser({email}), but a replica
    // read on the very next request can still miss it — one retry after 1.5s.
    if (anon && (res.status === 401 || res.status === 403)) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      res = await postRoutine();
    }

    if (!res.ok) {
      if (viaGate) {
        if (anon) {
          setGateError('Your account was created, but the routine could not be saved. Please try again.');
        } else if (res.status === 401 || res.status === 403) {
          setGateError(
            "You're already signed in on this browser, but this routine couldn't be created from here — open your dashboard and create it from Routines.",
          );
        } else {
          setGateError('The routine could not be saved. Please try again.');
        }
      } else {
        alert('Failed to create routine');
      }
      setLoading(false);
      return;
    }

    const { routine } = await res.json();

    // R3 — message-cohort possession redemption: when this wizard session was
    // seeded by an SMS/WhatsApp draft link, the inbound message already proved
    // the visitor holds the phone. Now that a session + profile exist, redeem
    // that evidence so their phone lands verified with no OTP (the voice
    // claim's trust model). Server-side it's atomic and a silent no-op for
    // voice/domain drafts, replays, and expired evidence — so this is safe to
    // fire for every draft token. Best-effort: a failure just means the
    // classic OTP fallback later.
    if (activeDraftToken) {
      try {
        await fetch('/api/onboarding/redeem-message-evidence', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ draftToken: activeDraftToken }),
        });
      } catch {
        // OTP fallback covers it.
      }
    }

    const inviteResult = await sendInvites(routine.id);
    // blocked_unverified needs no blocking alert: the redirect below carries
    // ?created=1&verify=1 and the arrival banner says exactly this, unmissably.
    if (inviteResult === 'failed') {
      alert(
        `Routine created, but the ${inviteCount} email invite${inviteCount === 1 ? '' : 's'} could not be sent. You can retry from the routine page (Members → Add).`,
      );
    }

    clearDraft();

    if (anon) {
      // Hard navigation on purpose: the fresh Pattern-A session was minted in
      // this tab after the app booted, so a full load lets the server re-read
      // auth cookies instead of trusting the stale client auth context.
      window.location.href = `/dashboard/routines/${routine.id}?created=1&verify=1`;
    } else {
      router.push(`/dashboard/routines/${routine.id}?created=1`);
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      await finishCreate({ anon: false, viaGate: false });
    } catch (err) {
      console.error('[PublicRoutineWizard] Error creating routine:', err);
      alert('Something went wrong');
      setLoading(false);
    }
  };

  /**
   * The Pattern-A email gate (anon path). Sequence:
   *
   *   1. Persist the draft synchronously — if anything below reloads the page
   *      (or the user bails to /login on a taken email), the wizard rehydrates
   *      exactly where they left off.
   *   2. Session guard — NEVER signInAnonymously over an existing session: a
   *      real (non-anonymous) session can reach this gate when the SSR authed
   *      check missed (profile/domain read failed), and minting an anon session
   *      would log them out. Real session → run the authed create directly.
   *      Existing ANON session (e.g. a remount after a prior attempt) → reuse it.
   *   3. signInAnonymously — mints an anonymous session, consumes the Turnstile
   *      captcha token, stamps created_via='email_signup' for the DB trigger.
   *      On failure the widget is remounted (captchaNonce) so the user can
   *      re-solve and retry immediately — the consumed token is single-use.
   *   4. updateUser({ email }) — Supabase queues the address as an email_change
   *      and sends the confirmation email; the trigger synchronously provisions
   *      public.users + a personal domain + owner membership INSIDE this call,
   *      which is what lets the very next POST /api/routines pass its
   *      owner/admin check.
   *   5. finishCreate — same 2-POST sequence as the dashboard wizard, with one
   *      1.5s retry on 401/403 for provisioning read lag.
   *
   * Taken email: updateUser fails → keep the draft, show the sign-in link back
   * to this page; on return the server marks authed=true and review shows the
   * plain Create button.
   *
   * Retries never repeat a completed auth step: anonSessionReady skips step 3
   * (a second anon user would be orphaned) and emailAttached skips steps 2-4
   * entirely (a second updateUser({email}) trips the 60s email rate limit) —
   * a retry after a failed routine POST goes straight to the create sequence.
   */
  const handleAnonCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = email.trim();
    if (!cleanEmail) return;
    if (captchaRequired && !captchaToken && !anonSessionReady) {
      setGateError('Please complete the verification challenge below.');
      return;
    }

    setLoading(true);
    setGateError(null);
    setEmailTaken(false);

    try {
      persistDraftNow();
      const supabase = createBrowserClient();

      if (emailAttached) {
        await finishCreate({ anon: true, viaGate: true });
        return;
      }

      let sessionReady = anonSessionReady;
      if (!sessionReady) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session && !session.user.is_anonymous) {
          await finishCreate({ anon: false, viaGate: true });
          return;
        }
        if (session?.user.is_anonymous) {
          sessionReady = true;
          setAnonSessionReady(true);
        }
      }

      if (!sessionReady) {
        const { error: anonError } = await supabase.auth.signInAnonymously({
          options: {
            captchaToken: captchaToken ?? undefined,
            // campaign (when present) lands the K-factor cohort in
            // auth.users.raw_user_meta_data next to created_via — funnel v1.
            data: { created_via: 'email_signup', ...(campaign ? { campaign } : {}) },
          },
        });
        setCaptchaToken(null);

        if (anonError) {
          console.error('[PublicRoutineWizard][PatternA] signInAnonymously failed:', anonError);
          setGateError(anonError.message || 'Failed to start your account.');
          setCaptchaNonce((n) => n + 1);
          setLoading(false);
          return;
        }
        setAnonSessionReady(true);
      }

      const { error: updateError } = await supabase.auth.updateUser({ email: cleanEmail });

      if (updateError) {
        console.error('[PublicRoutineWizard][PatternA] updateUser({email}) failed:', updateError);
        const message = updateError.message || '';
        if (updateError.code === 'email_exists' || /already/i.test(message)) {
          setEmailTaken(true);
        } else {
          setGateError(message || 'Failed to attach your email.');
        }
        setLoading(false);
        return;
      }
      setEmailAttached(true);

      await finishCreate({ anon: true, viaGate: true });
    } catch (err) {
      console.error('[PublicRoutineWizard][PatternA] Unexpected error:', err);
      setGateError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  // "Drafted for you" banner copy, by draft source.
  const draftOriginLabel = draftSpec
    ? draftSpec.sourceMeta?.domain
      ? draftSpec.sourceMeta.domain
      : draftSpec.source === 'voice_call' || draftSpec.source === 'phone_call'
        ? 'your conversation with Tessa'
        : draftSpec.companyName || null
    : null;

  return (
    <div className="space-y-6">
      {draftApplied && draftSpec && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
          <span className="font-semibold">
            Drafted {draftOriginLabel ? `from ${draftOriginLabel}` : 'for you'}
          </span>
          <span className="text-muted-foreground">
            {' '}— everything below is editable. Review, adjust, and launch.
          </span>
          {(draftSpec.industry || draftSpec.teamType) && (
            <span className="ml-2 inline-flex gap-1.5 align-middle">
              {draftSpec.industry && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  {draftSpec.industry}
                </span>
              )}
              {draftSpec.teamType && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  {draftSpec.teamType}
                </span>
              )}
            </span>
          )}
        </div>
      )}

      {/* Wave B: the deterministic "what you'll read every Monday" preview —
          only for domain-inferred drafts (the wizard's first-touch proof). */}
      {draftApplied && draftSpec && draftSpec.source === 'domain' && draftSpec.questions && (
        <SampleReportPreview
          routineName={draftSpec.name}
          companyName={draftSpec.companyName}
          questions={draftSpec.questions}
        />
      )}

      {/* Progress */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                ${i <= currentStepIndex ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
            >
              {i < currentStepIndex ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            {i < STEPS.length - 1 && (
              <div className={`w-4 sm:w-8 h-0.5 ${i < currentStepIndex ? 'bg-primary' : 'bg-muted'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Customize */}
      {step === 'customize' && (
        <Card>
          <CardHeader>
            <CardTitle>Brief Tessa</CardTitle>
            <CardDescription>
              Starting from {template.name} — you set the intent, Tessa runs the conversations.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
            </div>
            <div>
              <Label>Purpose (why does this routine exist?)</Label>
              <Textarea
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                rows={2}
                placeholder="e.g., Unblock shipping velocity for the engineering team"
              />
            </div>
            <div>
              <Label>Duration (minutes)</Label>
              <Input
                type="number"
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(parseInt(e.target.value) || 10)}
                min={5}
                max={60}
              />
            </div>
            <div>
              <Label>Persona (who conducts this?)</Label>
              <Select value={persona} onValueChange={setPersona}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {personaOptions.map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedPersona?.description && (
                <p className="text-xs text-muted-foreground mt-1">{selectedPersona.description}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Reach — channel intent. 'calls' promotes phone to the front
          of action_channels at save; 'async' keeps the template's own channels.
          Preselected from the template (a landing page's ?channel= overrides). */}
      {step === 'reach' && (
        <Card>
          <CardHeader>
            <CardTitle>How should Tessa reach your team?</CardTitle>
            <CardDescription>You can change this any time from the routine page</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => { setChannelIntent('calls'); setReachTouched(true); }}
                className={`flex flex-col items-start gap-2 p-5 border-2 rounded-[16px] text-left transition-colors
                  ${channelIntent === 'calls' ? 'border-[#EF4444] bg-[#EF4444]/5' : 'border-border hover:bg-muted'}`}
              >
                <Phone className={`h-5 w-5 ${channelIntent === 'calls' ? 'text-[#EF4444]' : 'text-muted-foreground'}`} />
                <p className="font-medium">Calls first</p>
                <p className="text-sm text-muted-foreground">
                  Tessa calls each person; Slack and email as fallback. You&apos;ll verify
                  your phone and get your team&apos;s number during launch.
                </p>
              </button>
              <button
                type="button"
                onClick={() => { setChannelIntent('async'); setReachTouched(true); }}
                className={`flex flex-col items-start gap-2 p-5 border-2 rounded-[16px] text-left transition-colors
                  ${channelIntent === 'async' ? 'border-[#EF4444] bg-[#EF4444]/5' : 'border-border hover:bg-muted'}`}
              >
                <MessageSquare className={`h-5 w-5 ${channelIntent === 'async' ? 'text-[#EF4444]' : 'text-muted-foreground'}`} />
                <p className="font-medium">Async first</p>
                <p className="text-sm text-muted-foreground">
                  Slack and email check-ins; add calling any time.
                </p>
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Questions */}
      {step === 'questions' && (
        <Card>
          <CardHeader>
            <CardTitle>What should Tessa ask?</CardTitle>
            <CardDescription>
              These are Tessa&apos;s talking points — she asks in her own words and follows the
              conversation where it goes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {questions.map((q, idx) => (
              <div key={idx} className="space-y-2 p-4 border rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">Q{idx + 1}</span>
                  <Input
                    value={q.question}
                    onChange={(e) => {
                      const next = [...questions];
                      next[idx] = { ...next[idx], question: e.target.value };
                      setQuestions(next);
                    }}
                  />
                </div>
                <Input
                  value={q.follow_up_prompt || ''}
                  onChange={(e) => {
                    const next = [...questions];
                    next[idx] = { ...next[idx], follow_up_prompt: e.target.value };
                    setQuestions(next);
                  }}
                  placeholder="Follow-up prompt (optional)"
                  className="text-sm"
                />
              </div>
            ))}
            <Button
              variant="outline"
              onClick={() =>
                setQuestions([...questions, { order: questions.length + 1, question: '', follow_up_prompt: '' }])
              }
            >
              Add Question
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 4: People — public flow has no roster; everyone joins by email */}
      {step === 'people' && (
        <Card>
          <CardHeader>
            <CardTitle>Who should Tessa check in with?</CardTitle>
            <CardDescription>
              Add names and emails — phone if you want Tessa to call. No account needed, always
              free for them.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <InviteByEmailSection entries={inviteEntries} onChange={handleInviteEntriesChange} />
            <p className="text-xs text-muted-foreground pt-1">
              How Tessa shows up: on the first touch she introduces herself, says who set this
              routine up and why — and what people share comes back to you as themes and
              blockers, never a score on a person.
            </p>
            {inviteCapHit && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                You can add up to {INVITE_BATCH_CAP} people here — more can join from the
                routine page once it&apos;s created.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 5: Schedule */}
      {step === 'schedule' && (
        <Card>
          <CardHeader>
            <CardTitle>Set the rhythm</CardTitle>
            <CardDescription>
              When should Tessa make her rounds? Consistency is the point — same time, every
              time.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScheduleEditor
              scheduleNatural={scheduleNatural}
              onScheduleNaturalChange={setScheduleNatural}
              scheduleCron={scheduleCron}
              onScheduleCronChange={setScheduleCron}
              timezone={timezone}
              onTimezoneChange={setTimezone}
            />
          </CardContent>
        </Card>
      )}

      {/* Step 6: Review */}
      {step === 'review' && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Here&apos;s the plan</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm">
                {scheduleNatural || 'On your schedule'}, Tessa checks in
                {inviteCount > 0
                  ? ` with ${inviteCount} ${inviteCount === 1 ? 'person' : 'people'}`
                  : ' with your team'}{' '}
                for ~{durationMinutes} minutes each — and reports back to you after every round.
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Name:</span> {name}</div>
                <div><span className="text-muted-foreground">Template:</span> {template.name}</div>
                <div><span className="text-muted-foreground">Duration:</span> {durationMinutes} min</div>
                <div><span className="text-muted-foreground">Questions:</span> {questions.length}</div>
                <div>
                  <span className="text-muted-foreground">People:</span> {inviteCount}
                  {inviteCount > 0 && (
                    <span className="text-muted-foreground"> (added by email)</span>
                  )}
                </div>
                <div><span className="text-muted-foreground">Reach:</span> {channelIntent === 'calls' ? 'Calls first' : 'Async first'}</div>
                <div><span className="text-muted-foreground">Schedule:</span> <span>{scheduleNatural || scheduleCron || 'Not set'}</span></div>
                <div><span className="text-muted-foreground">Timezone:</span> {timezone}</div>
                <div><span className="text-muted-foreground">Persona:</span> {selectedPersona?.label || persona.replace('_', ' ')}</div>
              </div>
              {purpose && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Purpose:</span> {purpose}
                </div>
              )}
            </CardContent>
          </Card>

          {!authed && (
            <Card>
              <CardHeader>
                <CardTitle>Where should Tessa report back?</CardTitle>
                <CardDescription>
                  After every round you get her report — trends, blockers, and who can unblock
                  whom. This creates your free workspace. No password — we&apos;ll email you a
                  confirmation link. 200 free minutes a month — no card to sign up; you&apos;ll
                  add one ($0, never charged automatically) when Tessa gets her phone number.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAnonCreate} className="space-y-4">
                  <div>
                    <Label htmlFor="gate-email">Your email</Label>
                    <Input
                      id="gate-email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="mt-1"
                      spellCheck="false"
                      translate="no"
                    />
                  </div>

                  {captchaSiteKey && (
                    <div className="flex justify-center">
                      <Turnstile
                        key={captchaNonce}
                        siteKey={captchaSiteKey}
                        onSuccess={setCaptchaToken}
                        onExpire={() => setCaptchaToken(null)}
                        onError={() => setCaptchaToken(null)}
                        options={{ theme: 'auto', size: 'flexible' }}
                      />
                    </div>
                  )}

                  {gateError && <p className="text-sm text-red-600">{gateError}</p>}

                  {emailTaken && (
                    <p className="text-sm">
                      Looks like you already have an account —{' '}
                      {/* ?next= (not ?redirect=): the OTP login flow only honors
                          `redirect` for /rooms/ links; everything else routes via
                          getSafeRedirectPath(next) — see LoginFormCompat/LoginClientCompat. */}
                      <Link
                        href={`/login?next=${encodeURIComponent(`/routines/new/${template.slug}`)}`}
                        className="font-medium underline underline-offset-2"
                      >
                        sign in
                      </Link>{' '}
                      and we&apos;ll finish creating this routine.
                    </p>
                  )}

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading || (captchaRequired && !captchaToken && !anonSessionReady)}
                  >
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                    <span>{loading ? 'Creating…' : 'Create my routine'}</span>
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    Tessa waits for your email confirmation before reaching out to anyone.
                  </p>
                </form>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={goBack} disabled={currentStepIndex === 0}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {step === 'review' ? (
          authed ? (
            <Button onClick={handleCreate} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              <span>{loading ? 'Creating...' : 'Create Routine'}</span>
            </Button>
          ) : null
        ) : (
          <Button onClick={goNext}>
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
